import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { errorLoginMessages, Method, methods } from "./Login";
import useLogin from "../../api/api-hooks/auth/useLogin";
import { useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import API from "../../api/api/API";
import PendingApprovalModal from "./PendingApprovalModal";
import { Alert } from "reactstrap";
import AuthAPI from "../../api/api/AuthAPI";
import { GetCloudToken } from "../../context/UserContext/useInitializeSession";
import { cloudLogoAtom, queriesEnabledAtom } from "../../atoms/GlobalAtoms";
import { useSetAtom } from "jotai";
import AdminNavbar from "../../components/Navbars/IndexNavbar";
import AppSpinner from "../../elements/AppSpinner";

export default function OAuthLoader() {
  const navigate = useNavigate();
  const [oauthError, setOauthError] = useState(false);
  const [method, setMethod] = useState<Method | "">("");
  const { loginError } = useLogin();
  const queryClient = useQueryClient();
  const [isPendingModalOpen, setPendingModalOpen] = useState(false);
  const setQueriesEnabled = useSetAtom(queriesEnabledAtom);
  const setCloudLogo = useSetAtom(cloudLogoAtom);
  const [search] = useSearchParams();

  useEffect(() => {
    const error = search.get("error");
    const m = search.get("method"); // Get OAuth method
    const type = search.get("type");
    const pending = search.get("pending");

    if (error && error === "true") {
      if (pending === "true") {
        return setPendingModalOpen(true);
      }

      setOauthError(true);

      if (m && (m === "amazon" || m === "office365" || m === "google")) {
        setMethod(m);
      }

      return;
    }

    if (loginError === errorLoginMessages.pendingApproval) {
      return setPendingModalOpen(true);
    }

    if (type === "registration") {
      const registrationToken = search.get("registrationToken");
      const appRedirectTo = search.get("appRedirectTo");

      if (!registrationToken) {
        return console.error("Failed to register user");
      }

      Cookies.set("registrationToken", registrationToken, {
        expires: 7,
        sameSite: "strict",
        secure: window.location.origin.startsWith("https"),
      });

      // For signing up from Emerge.ai/other apps
      if (appRedirectTo) {
        search.set("registrationToken", registrationToken);
        search.set("redirectTo", appRedirectTo);
      }

      let url = `/register/phone${
        !!search.toString() ? `?${search.toString()}` : ""
      }`;

      navigate(url);
    } else if (type === "authentication") {
      const authenticationToken = search.get("authenticationToken");
      const refreshToken = search.get("refreshToken");

      if (!authenticationToken || !refreshToken) {
        return console.error("Failed to authenticate user");
      }

      Cookies.set("authenticationToken", authenticationToken, {
        expires: 7,
        sameSite: "strict",
        secure: window.location.origin.startsWith("https"),
      });

      fetchClouds(authenticationToken, refreshToken);
    }
  }, [search, loginError]);

  if (!oauthError && !isPendingModalOpen) {
    return (
      <div className="tw:h-screen tw:flex tw:justify-center tw:items-center">
        <AppSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="tw:h-screen tw:flex tw:flex-col">
      <AdminNavbar />
      <div className="tw:p-8 tw:flex-1 tw:flex tw:flex-col tw:items-center tw:justify-center">
        {oauthError && (
          <Alert color="warning" className="tw:max-w-sm">
            <strong>Oops!</strong> We&apos;re having trouble using{" "}
            {method !== "" ? methods[method] : ""} to sign into your
            GrowthCloud.com account. Please try again.
          </Alert>
        )}
        <PendingApprovalModal
          open={isPendingModalOpen}
          onClose={handleModalClose}
        />
      </div>
    </div>
  );

  function handleModalClose() {
    setPendingModalOpen(false);
    navigate("/");
  }

  async function fetchClouds(authToken: string, refreshToken: string) {
    await AuthAPI.saveRefreshToken(authToken, refreshToken);

    queryClient.invalidateQueries({
      queryKey: ["user"],
    });

    let afterFetchURL = "/clouds";
    const redirectURL = localStorage.getItem("loginRedirectURL");
    localStorage.removeItem("loginRedirectURL");

    if (redirectURL) {
      try {
        const url = new URL(redirectURL);
        afterFetchURL = url.pathname + url.search;
      } catch {}
    } else {
      try {
        let lastVisitedCloud = sessionStorage.getItem("LastVisitedTenant");

        if (!lastVisitedCloud) {
          // Attempt to get last visited cloud from user object
          const data = await API.getUser();
          const user = data?.data?.data?.user;
          lastVisitedCloud = user?.LastVisitedTenant;

          if (lastVisitedCloud) {
            queryClient.setQueryData(
              ["user", lastVisitedCloud],
              (prev: any) => ({
                ...(prev || {}),
                user,
              })
            );
          }
        }

        if (lastVisitedCloud) {
          await GetCloudToken(
            lastVisitedCloud,
            setQueriesEnabled,
            setCloudLogo
          );

          afterFetchURL = `/clouds/${lastVisitedCloud}/activity`;
        }
      } catch {}
    }

    navigate(afterFetchURL);
  }
}
